<script setup lang="ts">
import AccordionComponent from "@/components/accordion/AccordionComponent.vue";
import { defineProps, inject, ref, defineModel, watch } from "vue";
import { DestinyMatrix, DestinyMatrixKeysEnum } from "@/types/destinyMatrix";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  matrix: DestinyMatrix | undefined;
  hasPremiumAccess: boolean;
}>();
const isOpen = defineModel<boolean>({ default: false });
const getDescription =
  inject<(key: DestinyMatrixKeysEnum) => Promise<never>>("getDescription");

const { t } = useI18n();

watch(
  () => props.matrix,
  () => (description.value = undefined)
);

const description = ref();

async function getData() {
  if (!props.hasPremiumAccess) {
    isOpen.value = false;
  }
  if (!description.value && props.matrix && getDescription) {
    description.value = await getDescription(
      DestinyMatrixKeysEnum.RELATIONS_IN_MATRIX
    );
  }
}
</script>

<template>
  <AccordionComponent
    type="white"
    @opened="getData"
    v-model="isOpen"
    :isBlocked="!hasPremiumAccess"
  >
    <template #title>
      {{ t("calculators.destiny-matrix.accordions.relations-in-matrix.title") }}
    </template>
    <template #faq-content>
      <div v-if="description" class="flex flex-col gap-2">
        <div class="whitespace-pre-line">
          {{
            t("calculators.destiny-matrix.accordions.relations-in-matrix.desc")
          }}
        </div>
        <p class="text-xl font-bold pt-4">
          {{
            t(
              "calculators.destiny-matrix.accordions.relations-in-matrix.partner-tasks-title",
              { value: matrix.r3.b }
            )
          }}
        </p>
        <div class="whitespace-pre-line">
          {{ description["partner_tasks"] }}
        </div>

        <p class="text-xl font-bold pt-4">
          {{
            t(
              "calculators.destiny-matrix.accordions.relations-in-matrix.suits-partner-title",
              { value: matrix.right_corner.l }
            )
          }}
        </p>
        <div class="whitespace-pre-line">
          {{ description["suits_partner"] }}
        </div>

        <p class="text-xl font-bold pt-4">
          {{
            t(
              "calculators.destiny-matrix.accordions.relations-in-matrix.where-meet-partner-title",
              { value: matrix.right_corner.l }
            )
          }}
        </p>
        <div class="whitespace-pre-line">
          {{ description["where_meet_partner"] }}
        </div>

        <p class="text-xl font-bold pt-4">
          {{
            t(
              "calculators.destiny-matrix.accordions.relations-in-matrix.relations-problems-title",
              { value: matrix.right_corner.c }
            )
          }}
        </p>
        <div class="whitespace-pre-line">
          {{ description["relations_problems"] }}
        </div>
      </div>
    </template>
    <template #collapse-text-btn>
      {{ t("calculators.destiny-matrix.accordions.collapse") }}
    </template>
  </AccordionComponent>
</template>
